import React, { useMemo, useState, useEffect } from 'react';
import { activityService } from '../services/activityService';
import { callMatchesUser, aggregateCallMetrics, CALL_OUTCOME_LABELS } from '../utils/callOutcomeAnalytics';
import { funnelConversionRates } from '../utils/funnelFromAudit';
import { buildLossCombos } from '../utils/lossCombos';

const RANGE_OPTS = [
  { id: '7d', label: '7 Tage' },
  { id: '30d', label: '30 Tage' },
  { id: '90d', label: '90 Tage' },
];

function rangeBounds(rangeId) {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const days = rangeId === '7d' ? 7 : rangeId === '90d' ? 90 : 30;
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function inRange(iso, start, end) {
  if (!iso) return false;
  const d = new Date(iso);
  return d >= start && d <= end;
}

function salesRoleKey(uid) {
  try {
    return localStorage.getItem(`crm_sales_role_${uid || 'anon'}`) || 'all';
  } catch {
    return 'all';
  }
}

function firstAuditMs(leadId, auditLog) {
  let best = null;
  for (const e of auditLog || []) {
    if (String(e.leadId) !== String(leadId)) continue;
    const t = new Date(e.timestamp).getTime();
    if (!Number.isFinite(t)) continue;
    if (best == null || t < best) best = t;
  }
  return best;
}

function medianSorted(nums) {
  const a = nums.filter(n => Number.isFinite(n)).sort((x, y) => x - y);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
}

function calendarMonthBounds(offsetMonthsAgo) {
  const ref = new Date();
  ref.setMonth(ref.getMonth() - offsetMonthsAgo);
  const start = new Date(ref.getFullYear(), ref.getMonth(), 1, 0, 0, 0, 0);
  const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 0, 23, 59, 59, 999);
  return { start, end };
}

export default function InsightsView({
  leads = [],
  meetings = [],
  auditLog = [],
  statusList = [],
  authUsers = [],
  currentUser,
}) {
  const [rangeId, setRangeId] = useState('30d');
  const [tab, setTab] = useState('leaderboard');
  const [roleFilter, setRoleFilter] = useState(() => salesRoleKey(currentUser?.id));

  useEffect(() => {
    setRoleFilter(salesRoleKey(currentUser?.id));
  }, [currentUser?.id]);

  useEffect(() => {
    try {
      localStorage.setItem(`crm_sales_role_${currentUser?.id || 'anon'}`, roleFilter);
    } catch { /* ignore */ }
  }, [roleFilter, currentUser?.id]);

  const { start, end } = useMemo(() => rangeBounds(rangeId), [rangeId]);

  const actsByLead = useMemo(() => activityService.getAll(), [leads, rangeId, tab]);

  const flatCalls = useMemo(() => {
    const out = [];
    Object.entries(actsByLead).forEach(([leadId, acts]) => {
      acts.forEach(a => {
        if (a.type !== 'call') return;
        const ts = a.ts || a.date;
        if (!ts) return;
        const d = new Date(ts);
        if (d < start || d > end) return;
        out.push({ ...a, leadId });
      });
    });
    return out;
  }, [actsByLead, start, end]);

  const filteredUsers = useMemo(() => {
    if (!authUsers.length) return currentUser ? [currentUser] : [];
    if (roleFilter === 'all') return authUsers;
    return authUsers.filter(u => {
      const r = (u.salesRole || u.user_metadata?.salesRole || '').toLowerCase();
      if (roleFilter === 'setter') return r === 'setter' || r === 'sdr';
      if (roleFilter === 'closer') return r === 'closer' || r === 'ae';
      return true;
    });
  }, [authUsers, currentUser, roleFilter]);

  const leaderboardRows = useMemo(() => {
    return filteredUsers.map(u => {
      const myCalls = flatCalls.filter(c => callMatchesUser(c, u.id, authUsers));
      const callM = aggregateCallMetrics(myCalls);
      const myMeetings = meetings.filter(m => {
        if (!inRange(m.start || m.date || m.createdAt, start, end)) return false;
        return m.ownerId === u.id || m.createdBy === u.id;
      });
      const revenue = leads
        .filter(l => l.offerStatus === 'angenommen' && (l.ownerId === u.id || l.ownerId == null))
        .reduce((s, l) => s + (Number(l.offerAmount) || 0), 0);
      return {
        id: u.id,
        name: u.name || u.email,
        calls: callM.total,
        termine: callM.termin,
        meetings: myMeetings.length,
        revenue,
      };
    }).sort((a, b) => b.calls - a.calls);
  }, [filteredUsers, flatCalls, meetings, leads, start, end, authUsers]);

  const funnelRows = useMemo(() => funnelConversionRates(auditLog, statusList).slice(0, 24), [auditLog, statusList]);

  const lossRows = useMemo(() => buildLossCombos(leads, actsByLead).slice(0, 20), [leads, actsByLead]);

  const sourceWin = useMemo(() => {
    const map = {};
    for (const l of leads) {
      const src = (l.source || '—').trim() || '—';
      if (!map[src]) map[src] = { won: 0, lost: 0 };
      if (l.offerStatus === 'angenommen') map[src].won += 1;
      if (l.offerStatus === 'abgelehnt' || String(l.status || '').toLowerCase().includes('verloren')) map[src].lost += 1;
    }
    return Object.entries(map)
      .map(([source, { won, lost }]) => {
        const c = won + lost;
        return { source, won, lost, pct: c ? Math.round((won / c) * 100) : 0, n: c };
      })
      .filter(x => x.n >= 2)
      .sort((a, b) => b.pct - a.pct || b.n - a.n)
      .slice(0, 15);
  }, [leads]);

  const cohortByHorizon = useMemo(() => {
    const DAY = 86400000;
    return [30, 60, 90].map(h => {
      let n = 0;
      let won = 0;
      for (const l of leads) {
        const f = firstAuditMs(l.id, auditLog);
        if (f == null) continue;
        if ((Date.now() - f) < h * DAY) continue;
        n += 1;
        if (l.offerStatus === 'angenommen') won += 1;
      }
      return { h, n, won, pct: n ? Math.round((won / n) * 100) : 0 };
    });
  }, [leads, auditLog]);

  const medianApptAndWon = useMemo(() => {
    const DAY = 86400000;
    const toAppt = [];
    const toWon = [];
    for (const l of leads) {
      const lid = String(l.id);
      const f = firstAuditMs(l.id, auditLog);
      if (f == null) continue;
      const apTimes = meetings.filter(m => String(m.leadId || m.lead?.id || '') === lid).map(m => new Date(m.start || m.date || m.createdAt).getTime()).filter(Number.isFinite);
      const firstAp = apTimes.length ? Math.min(...apTimes) : null;
      if (firstAp) toAppt.push((firstAp - f) / DAY);
      if (l.offerStatus === 'angenommen' && l._offerAcceptedAt) {
        const wts = new Date(l._offerAcceptedAt).getTime();
        if (Number.isFinite(wts)) toWon.push((wts - f) / DAY);
      }
    }
    return { appt: medianSorted(toAppt), won: medianSorted(toWon) };
  }, [leads, meetings, auditLog]);

  const medianBySource = useMemo(() => {
    const bySrc = {};
    for (const l of leads) {
      const src = (l.source || '—').trim() || '—';
      const f = firstAuditMs(l.id, auditLog);
      if (f == null || l.offerStatus !== 'angenommen' || !l._offerAcceptedAt) continue;
      const wts = new Date(l._offerAcceptedAt).getTime();
      if (!Number.isFinite(wts)) continue;
      if (!bySrc[src]) bySrc[src] = [];
      bySrc[src].push((wts - f) / 86400000);
    }
    return Object.entries(bySrc).map(([source, arr]) => ({
      source,
      n: arr.length,
      median: medianSorted(arr),
    })).filter(x => x.n >= 2).sort((a, b) => (a.median || 0) - (b.median || 0)).slice(0, 12);
  }, [leads, auditLog]);

  const momCalls = useMemo(() => {
    const cur = calendarMonthBounds(0);
    const prev = calendarMonthBounds(1);
    const countCalls = (s, e) => {
      let c = 0;
      Object.values(activityService.getAll()).forEach(list => {
        (list || []).forEach(a => {
          if (a.type !== 'call') return;
          const t = new Date(a.ts || a.date).getTime();
          if (!Number.isFinite(t) || t < s.getTime() || t > e.getTime()) return;
          c += 1;
        });
      });
      return c;
    };
    const thisM = countCalls(cur.start, cur.end);
    const lastM = countCalls(prev.start, prev.end);
    const delta = lastM ? Math.round(((thisM - lastM) / lastM) * 100) : null;
    return { thisM, lastM, delta };
  }, [leads, auditLog, meetings]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg)', fontFamily: 'var(--font)' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 6, flexShrink: 0, background: 'var(--bg)', borderBottom: '1px solid var(--border)', padding: '14px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Insights</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>Leaderboards, Trichter, Verluste, Quellen — plus Kohorten und Zeiträume</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={rangeId} onChange={e => setRangeId(e.target.value)} style={{ height: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', padding: '0 10px' }}>
              {RANGE_OPTS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} title="Filtert Nutzer in Leaderboards (Profil-Feld salesRole)"
              style={{ height: 36, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', padding: '0 10px' }}>
              <option value="all">Alle Rollen</option>
              <option value="setter">Setter / SDR</option>
              <option value="closer">Closer / AE</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { id: 'leaderboard', label: 'Leaderboards' },
            { id: 'funnel', label: 'Trichter-Konversion' },
            { id: 'loss', label: 'Verlust-Kombinationen' },
            { id: 'source', label: 'Quelle → Gewinnquote' },
            { id: 'analytics', label: 'Kohorten & Zeiten' },
          ].map(t => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: tab === t.id ? '1px solid var(--accent)' : '1px solid var(--border)',
                background: tab === t.id ? 'var(--accent-bg)' : 'var(--bg2)', color: tab === t.id ? 'var(--accent)' : 'var(--text2)',
                fontWeight: tab === t.id ? 700 : 500, cursor: 'pointer', fontSize: 13,
              }}>{t.label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 24px', WebkitOverflowScrolling: 'touch' }}>
      {tab === 'leaderboard' && (
        <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Nutzer im Zeitraum</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text3)', fontSize: 11 }}>
                  <th style={{ padding: '8px 6px' }}>Nutzer</th>
                  <th style={{ padding: '8px 6px' }}>Anrufe</th>
                  <th style={{ padding: '8px 6px' }}>Termine (Call)</th>
                  <th style={{ padding: '8px 6px' }}>Meetings</th>
                  <th style={{ padding: '8px 6px' }}>Umsatz gewonnen</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardRows.map(row => (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>{row.name}</td>
                    <td style={{ padding: '10px 6px', fontFamily: 'var(--mono)' }}>{row.calls}</td>
                    <td style={{ padding: '10px 6px', fontFamily: 'var(--mono)' }}>{row.termine}</td>
                    <td style={{ padding: '10px 6px', fontFamily: 'var(--mono)' }}>{row.meetings}</td>
                    <td style={{ padding: '10px 6px', fontFamily: 'var(--mono)' }}>{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(row.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10 }}>Umsatz = Summe Angebotsbetrag bei gewonnenen Deals (`offerStatus = angenommen`), MVP ohne strikte Umsatz-Zuordnung pro Nutzer.</div>
        </div>
      )}

      {tab === 'funnel' && (
        <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Status-Übergänge (Audit-Log)</div>
          {funnelRows.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Noch keine Status-Änderungen im Audit — Status an Leads ändern (mit Audit-Feldern), dann erscheinen Konversionen.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {funnelRows.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, fontSize: 13 }}><strong>{r.labelFrom}</strong> → <strong>{r.labelTo}</strong></div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>{r.count}×</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', minWidth: 48, textAlign: 'right' }}>{r.pctOfFrom}%</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', minWidth: 120 }}>Anteil von „{r.labelFrom}“</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'loss' && (
        <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Häufigste Kombinationen</div>
          {lossRows.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Keine verlorenen Deals mit Verlustgrund / Branche.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {lossRows.map((r, i) => (
                <div key={i} style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ fontWeight: 700, color: '#dc2626' }}>{r.count}×</span>
                  {' '}<strong>{r.lossReason}</strong>
                  <span style={{ color: 'var(--text3)' }}> · Branche: {r.branch}</span>
                  <span style={{ color: 'var(--text3)' }}> · letzter Call: {CALL_OUTCOME_LABELS[r.lastCallSlug] || r.lastCallSlug}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'source' && (
        <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Gewinnquote nach Lead-Quelle (`source`)</div>
          {sourceWin.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lege am Lead „Quelle“ fest — mindestens 2 abgeschlossene Deals (gewonnen+verloren) pro Quelle.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text3)', fontSize: 11 }}>
                  <th style={{ padding: '8px 6px' }}>Quelle</th>
                  <th style={{ padding: '8px 6px' }}>Gewonnen</th>
                  <th style={{ padding: '8px 6px' }}>Verloren</th>
                  <th style={{ padding: '8px 6px' }}>Quote</th>
                </tr>
              </thead>
              <tbody>
                {sourceWin.map(r => (
                  <tr key={r.source} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>{r.source}</td>
                    <td style={{ padding: '10px 6px' }}>{r.won}</td>
                    <td style={{ padding: '10px 6px' }}>{r.lost}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 700, color: 'var(--accent)' }}>{r.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'analytics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Kohorte (Audit-Erstkontakt, gereift)</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>Nur Leads mit erstem Audit-Eintrag. „Gereift“ = mindestens H Tage seit erstem Audit. Gewonnen = aktuell `offerStatus = angenommen`.</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text3)', fontSize: 11 }}>
                  <th style={{ padding: '8px 6px' }}>Horizont</th>
                  <th style={{ padding: '8px 6px' }}>Leads (gereift)</th>
                  <th style={{ padding: '8px 6px' }}>Gewonnen (Snapshot)</th>
                  <th style={{ padding: '8px 6px' }}>Quote</th>
                </tr>
              </thead>
              <tbody>
                {cohortByHorizon.map(row => (
                  <tr key={row.h} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 6px', fontWeight: 600 }}>{row.h} Tage</td>
                    <td style={{ padding: '10px 6px' }}>{row.n}</td>
                    <td style={{ padding: '10px 6px' }}>{row.won}</td>
                    <td style={{ padding: '10px 6px', fontWeight: 700, color: 'var(--accent)' }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Median-Tage (erster Audit → …)</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
              <div>Erstes Meeting: <strong>{medianApptAndWon.appt != null ? `${medianApptAndWon.appt.toFixed(1)} Tage` : '—'}</strong> (nur mit Termin-Datum)</div>
              <div>Gewonnen (`_offerAcceptedAt`): <strong>{medianApptAndWon.won != null ? `${medianApptAndWon.won.toFixed(1)} Tage` : '—'}</strong></div>
            </div>
            {medianBySource.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text3)', fontSize: 11 }}>
                    <th style={{ padding: '8px 6px' }}>Quelle</th>
                    <th style={{ padding: '8px 6px' }}>n</th>
                    <th style={{ padding: '8px 6px' }}>Median Tage bis Won</th>
                  </tr>
                </thead>
                <tbody>
                  {medianBySource.map(r => (
                    <tr key={r.source} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 6px', fontWeight: 600 }}>{r.source}</td>
                      <td style={{ padding: '10px 6px' }}>{r.n}</td>
                      <td style={{ padding: '10px 6px', fontFamily: 'var(--mono)' }}>{r.median != null ? r.median.toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="gl-card" style={{ borderRadius: 16, padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Anrufe: Kalendermonat vs. Vormonat</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, color: 'var(--text2)' }}>
              <div>Aktueller Monat: <strong style={{ color: 'var(--text)' }}>{momCalls.thisM}</strong></div>
              <div>Vormonat: <strong style={{ color: 'var(--text)' }}>{momCalls.lastM}</strong></div>
              <div>Δ: <strong style={{ color: momCalls.delta >= 0 ? '#16a34a' : '#dc2626' }}>{momCalls.delta == null ? '—' : `${momCalls.delta >= 0 ? '+' : ''}${momCalls.delta}%`}</strong></div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
